import { motion } from 'motion/react'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import MaterialShowcase from '#app/components/marketing/material-showcase.tsx'
import PropertiesShowcase from '#app/components/marketing/properties-showcase.tsx'
import MarketingSpacer from '#app/components/marketing/spacer.tsx'
import SplitText from '#app/components/motion/split-text.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { useChangeBackgroundColor } from '#app/hooks/use-change-background-color.ts'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import { generateMetaTranslationLoader } from '#app/utils/i18next.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import ABrandOfICG from '/img/what-is-sapienstone/icg/A BRAND OF ICG.svg'
import Espesor from '/img/what-is-sapienstone/dimensions/espesor.svg'
import { type Route } from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types/what-is-sapienstone.ts'

export async function loader({ request }: Route.LoaderArgs) {
	return {
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'sapienstone',
			keys: {
				title: 'title',
				description: 'description',
				canonical: `${BASE_URL}${routes.public.about.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

function AnimatedImageReveal() {
	return (
		<motion.div
			className={'relative h-full w-full'}
			initial={'hidden'}
			whileInView={'visible'}
			viewport={{
				once: true,
				amount: 0.3,
			}}
		>
			{/* Top */}
			<motion.div
				className={'absolute left-0 top-0 !z-0 h-0.5 bg-brand-dusk'}
				variants={{
					hidden: { width: 0 },
					visible: { width: '100%' },
				}}
				transition={{
					duration: 1.2,
					ease: [0.25, 0.1, 0.25, 1],
					delay: 0,
				}}
			/>
			<motion.div
				variants={{
					hidden: { height: 0 },
					visible: { height: '100%' },
				}}
				transition={{
					delay: 1.4,
					duration: 1.2,
					ease: [0.25, 0.1, 0.25, 1],
				}}
				className={'absolute right-0 top-0 !z-0 w-0.5 bg-brand-dusk'}
			/>
			<motion.div
				variants={{
					hidden: { width: 0 },
					visible: { width: '100%' },
				}}
				transition={{
					delay: 2.8,
					duration: 1.2,
					ease: [0.25, 0.1, 0.25, 1],
				}}
				className={'absolute bottom-0 right-0 !z-0 h-0.5 bg-brand-dusk'}
			/>
			<motion.img
				className={
					'absolute inset-0 z-20 block h-full w-full object-cover opacity-0'
				}
				variants={{
					hidden: {
						x: '-100%',
						opacity: 0,
					},
					visible: {
						x: 0,
						opacity: 1,
					},
				}}
				transition={{
					duration: 1.8,
					ease: [0.25, 0.1, 0.25, 1],
					delay: 4.6,
				}}
				alt={'Sapienstone Quartzite Background'}
				src={
					'/img/what-is-sapienstone/dimensions/ss-ss02-quarzite_vals-_-strut-320160-f1.webp'
				}
			/>
			<motion.div
				variants={{
					hidden: { opacity: 0 },
					visible: {
						opacity: 1,
					},
				}}
				transition={{
					delay: 6,
					duration: 1.4,
					ease: [0.25, 0.1, 0.25, 1],
				}}
				className={cn(
					'!z-30 flex w-fit flex-col items-end justify-end opacity-0',
					'absolute bottom-0 right-0 translate-x-1/2',
					'text-brand-dusk',
				)}
			>
				<p
					className={
						'flex flex-col font-serif text-h1 !font-light md:text-mega xl:text-giga 2xl:text-[12vh] 2xl:leading-[9.5vh]'
					}
				>
					<span className={'block'}>160x</span>
					<span className={'block'}>
						320
						<span className={'text-[3.5vw] xl:text-[2.5vw]'}>cm</span>
					</span>
				</p>
				<p
					className={
						'text-body-xs font-semibold tracking-[0.2em] xl:text-body-sm'
					}
				>
					126”x63”
				</p>
			</motion.div>
		</motion.div>
	)
}

export default function WhatIsSapienstonePage() {
	const { t } = useTranslation('sapienstone')
	const innovationRef = useChangeBackgroundColor(
		extendedTheme.colors.background,
	)

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `Sapienstone.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<section
				className={
					'grid h-screen w-full max-w-[100vw] grid-cols-12 grid-rows-12 lg:max-w-[calc(100vw-100px)]'
				}
			>
				<div
					className={
						'col-start-3 -col-end-2 row-start-3 -row-end-3 lg:col-start-6'
					}
				>
					<div
						className={cn(
							'text-brand-dusk',
							'flex flex-col justify-between gap-[20vh] border-l border-brand-dusk pl-md xl:gap-[18vh] xl:pl-lg',
						)}
					>
						<Text as={'h2'} size={'h2'} weight={'light'} type={'serif'}>
							<SplitText as={'span'}>“Haute couture surfaces.”</SplitText>
						</Text>
						<MarketingImage
							src={'/img/what-is-sapienstone/2-HAUTE COUTURE SURFACES.webp'}
						/>
					</div>
				</div>
			</section>

			<MarketingSpacer side={'top'} hideOnMobile />

			<section
				className={
					'grid max-w-[100vw] grid-cols-12 grid-rows-[repeat(12,10vh)] lg:max-w-[calc(100vw-100px)]'
				}
			>
				<div
					className={cn(
						'col-start-2 -col-end-2 md:col-start-3 xl:col-end-7 2xl:col-end-8',
						'text-brand-dusk',
						'flex flex-col gap-sm lg:gap-md',
					)}
				>
					<Text
						as={'h2'}
						size={'h2'}
						className={'-space-y-2 uppercase'}
						weight={'light'}
						type={'serif'}
					>
						<SplitText as={'span'}>
							<Trans>{t('sizes.title')}</Trans>
						</SplitText>
					</Text>
					<Text as={'p'} size={'p'}>
						<SplitText as={'span'}>
							<Trans>{t('sizes.description')}</Trans>
						</SplitText>
					</Text>
				</div>

				<div
					className={cn(
						'relative flex h-full w-full',
						'col-start-1 col-end-10 row-start-4 row-end-8 xl:col-end-7 xl:row-start-5 xl:row-end-9 2xl:col-end-6',
					)}
				>
					<AnimatedImageReveal />
				</div>

				<div
					className={
						'col-start-3 -col-end-3 row-start-9 -row-end-1 overflow-hidden sm:-col-end-6 lg:col-start-7 lg:-col-end-1 xl:col-start-9 xl:row-start-5 xl:-row-end-2 2xl:col-start-8'
					}
				>
					<div
						className={cn(
							'text-brand-dusk',
							'flex flex-col justify-between gap-xl border-l border-brand-dusk pl-md xl:gap-[10vh] xl:pl-lg',
						)}
					>
						<div className={'flex items-center gap-xxl'}>
							<div>
								<h2
									className={
										'font-serif text-h3 !font-light md:text-h2 xl:text-h1'
									}
								>
									12mm
									<br />
									20mm
								</h2>
								<p
									className={
										'text-body-xs font-semibold tracking-[0.2em] xl:text-body-sm'
									}
								>
									1/2”
									<br />
									3/4”
								</p>
							</div>
							<img
								className={'h-[min(15vh,150px)]'}
								src={Espesor}
								alt={'A brand of Iris Cerámica Group'}
							/>
						</div>
						<MarketingImage
							alt={'Thickness'}
							imageClassName={
								'h-full max-h-[50vh] w-full object-cover object-[50%,100%]'
							}
							src={'/img/what-is-sapienstone/dimensions/SAPIENSTONE_4D_1.webp'}
						/>
					</div>
				</div>
			</section>

			<MarketingSpacer side={'bottom'} />

			<MaterialShowcase />

			<PropertiesShowcase
				sectionTitle={t('advantages.title')}
				showcaseItems={[
					{
						title: t('advantages.1.title'),
						image: '/img/advantages/stain-resistant.webp',
					},
					{
						title: t('advantages.2.title'),
						image: '/img/advantages/uv-resistant.webp',
					},
					{
						title: t('advantages.3.title'),
						image: '/img/advantages/chemical-resistant.webp',
					},
					{
						title: t('advantages.4.title'),
						image: '/img/advantages/scratch-resistant.webp',
					},
					{
						title: t('advantages.5.title'),
						image: '/img/advantages/hygienic.webp',
					},
					{
						title: t('advantages.6.title'),
						image: '/img/advantages/easy-to-clean.webp',
					},
					{
						title: t('advantages.7.title'),
						image: '/img/advantages/fire-resistant.webp',
					},
					{
						title: t('advantages.8.title'),
						image: '/img/advantages/abrasion-resistant.webp',
					},
				]}
			/>

			<section ref={innovationRef}>
				<div
					className={
						'false-center-on-desktop max-w-[100vw] py-xxxl text-brand-dusk lg:max-w-[calc(100vw-100px)] lg:py-[15vh]'
					}
				>
					<div
						className={
							'relative mx-auto flex w-fit flex-col items-center gap-[4vh]'
						}
					>
						<h2
							className={
								'2xl:text- w-fit font-serif text-h1 !font-thin lg:text-mega xl:text-giga 2xl:text-huge'
							}
						>
							<span className={'block translate-x-[12%]'}>WHAT</span>{' '}
							<span className={'-mt-sm block'}>WE STAND</span>{' '}
							<span className={'-mt-sm block translate-x-[40%]'}>FOR</span>
						</h2>
						<div
							className={
								'absolute -right-sm bottom-0 h-[70%] w-px bg-brand-dusk xl:-right-md'
							}
						/>
						<p className={'self-end font-serif font-thin xl:text-2xl'}>
							{t('values.our')}
						</p>
					</div>
				</div>

				<div
					className={
						'relative grid h-screen max-w-[100vw] grid-cols-12 grid-rows-12 lg:max-w-[calc(100vw-100px)]'
					}
				>
					<MarketingImage
						className={
							'col-start-2 col-end-6 row-start-1 row-end-4 !opacity-70 lg:row-end-5 xl:!opacity-90'
						}
						src={
							'/img/what-is-sapienstone/what-we-stand-for/Captura de pantalla 2024-08-21 a las 17.10.41.webp'
						}
					/>
					<MarketingImage
						className={
							'col-start-7 -col-end-2 row-start-1 row-end-6 !opacity-70 lg:col-start-8 xl:!-scale-x-100 xl:!opacity-90'
						}
						src={
							'/img/what-is-sapienstone/what-we-stand-for/pexels-filmjos__e-19910453.webp'
						}
					/>
					<MarketingImage
						className={
							'col-start-4 col-end-11 row-start-6 -row-end-2 !opacity-70 lg:col-end-7 lg:row-start-6 xl:!opacity-90'
						}
						src={
							'/img/what-is-sapienstone/what-we-stand-for/pexels-jasinto-shabani-1609538378-27584800.webp'
						}
					/>
					<div
						className={
							'false-center-on-desktop absolute inset-0 z-10 col-span-full mx-auto flex w-fit flex-col items-center gap-sm font-serif text-h3 !font-light text-brand-dusk min-[450px]:text-h2 md:text-h1 2xl:text-mega'
						}
					>
						<LocalizedLink
							style={{
								textDecorationThickness: '4px',
							}}
							to={'#'}
							// routes.public.landings.design.route({})
							className={''}
						>
							{t('values.design')}
						</LocalizedLink>
						<LocalizedLink
							style={{
								textDecorationThickness: '4px',
							}}
							to={routes.public.landings.innovation.route({})}
							className={'hover:underline'}
						>
							{t('values.innovation')}
						</LocalizedLink>
						<LocalizedLink
							style={{
								textDecorationThickness: '4px',
							}}
							to={routes.public.landings.sustainability.route({})}
							className={'hover:underline'}
						>
							{t('values.sustainability')}
						</LocalizedLink>
					</div>
				</div>
			</section>

			<LandingSection
				borderToBorderOnX
				backgroundColor={extendedTheme.colors.brand.mist.DEFAULT}
				className={
					'flex max-w-[100vw] flex-col items-center py-xxxl lg:max-w-[calc(100vw-100px)] lg:py-[20vh]'
				}
			>
				<div className={'mx-auto'}>
					<img
						className={'w-[min(50vw,400px)]'}
						src={ABrandOfICG}
						alt={'A brand of Iris Cerámica Group'}
					/>
				</div>
				<div
					className={
						'grid grid-cols-12 grid-rows-[repeat(14,9vh)] md:grid-rows-[repeat(14,7vh)] lg:grid-rows-[repeat(14,5.5vh)] xl:grid-rows-[repeat(14,9vh)]'
					}
				>
					<div
						className={cn(
							'col-start-2 -col-end-2 row-start-2',
							'md:col-end-7 md:gap-md',
							'xl:row-start-3',
							'2xl:col-end-7',
							'flex flex-col gap-sm',
						)}
					>
						<Text as={'h2'} size={'h4'} type={'serif'} weight={'light'}>
							<SplitText as={'span'}>
								<Trans>“{t('leader.title')}”</Trans>
							</SplitText>
						</Text>
						<Text as={'p'} size={'p'} weight={'light'}>
							<SplitText delay={0.4} as={'span'}>
								<Trans>{t('leader.description')}</Trans>
							</SplitText>
						</Text>
					</div>

					<div
						className={cn(
							'col-start-2 -col-end-2 row-start-10',
							'md:col-start-3 md:row-start-8',
							'xl:col-end-8 xl:row-start-7',
						)}
					>
						<div
							className={cn(
								'text-brand-dusk',
								'flex flex-col justify-between gap-xl border-l border-brand-dusk pl-md md:gap-xxl xl:gap-[12vh] xl:pl-lg',
							)}
						>
							<MarketingImage
								imageClassName={
									'h-[min(24vh,400px)] xl:h-[50vh] object-[80%,70%] object-cover'
								}
								src={
									'/img/what-is-sapienstone/icg/ir-bda-_-_-_-_-amb6-trademark_tiles.webp'
								}
							/>
							<div className={'flex flex-col gap-sm'}>
								<Text as={'h2'} size={'h4'} type={'serif'} weight={'light'}>
									<SplitText as={'span'}>
										<Trans>“{t('reengineering.title')}”</Trans>
									</SplitText>
								</Text>
								<Text as={'p'} size={'p'} weight={'light'}>
									<SplitText delay={0.4} as={'span'}>
										<Trans>{t('reengineering.description')}</Trans>
									</SplitText>
								</Text>
							</div>
						</div>
					</div>

					<MarketingImage
						className={cn(
							'col-start-4 -col-end-1 row-span-4 row-start-5',
							'md:col-start-8 md:row-start-3',
							'xl:col-start-9 xl:row-span-5 xl:row-start-4',
						)}
						src={
							'/img/what-is-sapienstone/icg/icg-showroom_gallery-milano-it-2020-ref1_part5-external.webp'
						}
					/>
				</div>
			</LandingSection>
		</>
	)
}
